/**
 * Resolves the API base URL. Priority:
 *   1. window.__THALIOS_CONFIG__.apiUrl  (runtime-config.js — retarget without rebuild)
 *   2. import.meta.env.VITE_API_BASE_URL (compile time)
 *   3. '/api'                            (same-origin; dev proxy forwards it)
 */
interface RuntimeConfig {
  apiUrl?: string;
  /** Local/dev API used automatically if `apiUrl` (the live host) is unreachable. */
  apiUrlFallback?: string;
  tenantCode?: string;
}

declare global {
  interface Window {
    __THALIOS_CONFIG__?: RuntimeConfig;
  }
}

export function resolveApiBaseUrl(): string {
  if (typeof window !== 'undefined' && window.__THALIOS_CONFIG__?.apiUrl) {
    return window.__THALIOS_CONFIG__.apiUrl;
  }
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return env?.VITE_API_BASE_URL ?? '/api';
}

/**
 * Local/dev API base to fall back to when the live host is offline. Priority:
 *   1. window.__THALIOS_CONFIG__.apiUrlFallback  (runtime-config.js)
 *   2. import.meta.env.VITE_API_FALLBACK_URL      (compile time)
 *   3. '/api'                                     (same-origin proxy)
 */
export function resolveApiFallbackUrl(): string {
  if (typeof window !== 'undefined' && window.__THALIOS_CONFIG__?.apiUrlFallback) {
    return window.__THALIOS_CONFIG__.apiUrlFallback;
  }
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return env?.VITE_API_FALLBACK_URL ?? '/api';
}

/**
 * Resolves the tenant code. Priority:
 *   1. The site's SUBDOMAIN — e.g. `spicegarden.kristysoftware.com` → `spicegarden`
 *      (multi-tenant by domain; each restaurant gets its own subdomain).
 *   2. window.__THALIOS_CONFIG__.tenantCode  (runtime-config.js override)
 *   3. VITE_DEFAULT_TENANT_CODE / 'thalios'  (dev / localhost fallback)
 */
export function resolveTenantCode(): string {
  if (typeof window !== 'undefined') {
    const sub = subdomainOf(window.location.hostname);
    if (sub) return sub;
    if (window.__THALIOS_CONFIG__?.tenantCode) return window.__THALIOS_CONFIG__.tenantCode;
  }
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  return env?.VITE_DEFAULT_TENANT_CODE ?? 'thalios';
}

/** The tenant subdomain from a hostname, or null for apex/www/localhost/IP. */
function subdomainOf(host: string): string | null {
  const h = (host || '').toLowerCase();
  if (!h || h === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(h)) return null;
  const parts = h.split('.');
  if (parts.length < 3) return null; // apex domain (e.g. example.com) — no subdomain
  const sub = parts[0];
  if (!sub || sub === 'www' || sub === 'api') return null;
  return sub;
}
